import { memo, useCallback, useContext, useEffect } from "react";
import Cookies from "js-cookie";
import { useTranslator } from "@eo-locale/react";

import { isEmpty } from "lodash";
import { useRouter } from "next/router";

import { buildImgixUrl, dateWithSiteLocale, stripAllHtml } from "@/lib";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  CloseButton,
  Contribution,
  HStack,
  Heading,
  useMediaQuery
} from "@/storybook-components";

import ActionList from "@/components/places/esri-map/action-items";
import PinIcon from "@/components/places/esri-map/markers/pin-Icon";

import { EsriContext } from "@/components/places/context/esri-context";
import { EsriContextType } from "@/components/places/context/esri-context-type";

import ViewPinDetails from "@/components/places/esri-map/view-pin-details";

import useVotes from "@/hooks/places/use-votes";
import useUser from "@/hooks/useUser";
import {
  resetMarker,
  setPreviousMarker
} from "@/redux/features/place-marker/slice";

import {
  setPinSearchFromUrl,
  setView
} from "@/redux/features/place-right-panel/slice";
import {
  setMapCenterCoordinates,
  setPinDetails
} from "@/redux/features/place/slice";
import usePinView from "@/hooks/places/use-pin-view";
import {
  setTriggerPoint,
  setUnverifiedModalOpen
} from "@/redux/features/unverified-modal/slice";
import useAccessStatus from "@/hooks/places/use-access-status";
import { setMarkers } from "@/redux/features/place-markers/slice";
import useShowUnverifiedModal from "@/hooks/places/use-unverfied-modal";
const MarkerDetails = ({
  rightPanelProps,
  currentPageRef,
  setCurrentPageNumber
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);

  const pinDetails = useAppSelector((state) => state.place.pinDetails);
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const pinSearchFromUrl = useAppSelector(
    (state) => state.placesRightPanelView.pinSearchFromUrl
  );
  const { mapRef } = useContext(EsriContext) as EsriContextType;
  const { isRegisteredUserLoggedIn, isUnverfiedUserLoggedIn } = useUser();
  const { accessStatus } = useAccessStatus();
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  const { language, translate } = useTranslator();
  const isAddressVisible = useAppSelector(
    (state) => state.place.place.showAddress
  );
  const { manageVotes, fetchPinDetailsWithSelectedData, findCurrentLikedPin } =
    useVotes();
  const centerCoordinates = useAppSelector(
    (state) => state.place.centerCoordinates
  );

  const router = useRouter();
  const { viewCardLoader } = usePinView({});
  const actionParams = {
    projectId,
    placesId,
    markerId: ""
  };
  const { asPath } = router;
  const { showUnverifiedModal } = useShowUnverifiedModal({ isNewPin: false });

  const handleVoteUp = (id) => {
    if (showUnverifiedModal()) return;
    if (accessStatus) {
      if (!isRegisteredUserLoggedIn && !isUnverfiedUserLoggedIn) {
        const filteredPin = findCurrentLikedPin(id, rightPanelView);
        const triggerPoint = {
          projectId,
          toolId: placesId,
          postId: id,
          triggeredSource: "vote",
          voteStatus: !filteredPin?.voted
        };
        Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
          expires: 1,
          secure: true,
          sameSite: "Strict"
        });
        dispatch(setTriggerPoint(triggerPoint));
        dispatch(setUnverifiedModalOpen(true));
        return;
      }
    }

    manageVotes?.(id, rightPanelView);
  };

  useEffect(() => {
    isMobile && mapRef?.closePopup();
  }, [isMobile]);

  useEffect(() => {
    const updateMap = async () => {
      await dispatch(resetMarker());

      dispatch(
        setMapCenterCoordinates({
          lat: mapRef?.getCenter().lat,
          lng: mapRef?.getCenter().lng,
          zoom: mapRef?.getZoom()
        })
      );
      await mapRef?.setView([pinDetails?.lat, pinDetails?.lng], 20);
    };

    !isEmpty(pinDetails) && pinSearchFromUrl && updateMap();
  }, [pinDetails, mapRef, pinSearchFromUrl]);

  const viewPinDetails: any = useCallback(() => {
    const wrapperPadding = {
      pl: 4,
      pr: 4,
      pt: 6,
      pb: isMobile ? 100 : 4
    };

    const {
      id,
      address,
      comment,
      fileUrl,
      pinIcon,
      pinColor,
      pinName,
      userImageUrl,
      createdAt,
      author,
      markerUserId,
      voted,
      totalVotes
    } = pinDetails;

    const imageUrl = fileUrl?.includes("imgix.net")
      ? fileUrl
      : buildImgixUrl(fileUrl, 800, 800);

    const pinIconProps = {
      pinColor: pinColor,
      pinIcon: pinIcon,
      pinId: id
    };

    return (
      <Box {...wrapperPadding} key={id} tabIndex={0}>
        <Contribution
          key={id}
          cardVariant="unstyled"
          username={author}
          contributionTime={dateWithSiteLocale(
            createdAt,
            language.toLowerCase()
          )}
          isAddressVisible={isAddressVisible}
          imageSrc={imageUrl}
          imageAlt={pinName}
          showImage={!!fileUrl}
          cardDescription={comment ? stripAllHtml(comment) : ""}
          showDescription
          userAvatarSrc={userImageUrl}
          showFooter={true}
          pinType={pinName || "Pin"}
          pinAddress={address}
          tileBackgroundColor="gray.100"
          isLoaded={viewCardLoader || !isEmpty(pinDetails)}
          pinColor={pinColor}
          cardHeading=""
          commentsCount={0}
          showCommentIcon={false}
          isThumbsUp={true}
          thumbsUpCount={totalVotes || 0}
          shareText=""
          hasBodyHighlight={false}
          showMoreOptions={true}
          showDetailedText
          isVoteActive={voted}
          isIconButtonPressed={voted}
          actionList={
            <ActionList
              actionParams={{
                ...actionParams,
                markerId: id,
                userId: markerUserId
              }}
              rightPanelProps={rightPanelProps}
            />
          }
          pinIcon={<PinIcon {...pinIconProps} />}
          handleVoteUp={() => !isEmpty(pinDetails) && handleVoteUp(id)}
          dataElement={
            <ViewPinDetails
              pinDetails={pinDetails}
              viewCardLoader={!isEmpty(pinDetails) && viewCardLoader}
            />
          }
          magnifiedImage={true}
          ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
            votesCount: totalVotes
          })} ${
            voted ? translate("common.removeVote") : translate("common.addVote")
          }`}
        />
      </Box>
    );
  }, [pinDetails, isMobile, rightPanelView, manageVotes]);

  return (
    <>
      <>
        <HStack>
          <Box
            width={isMobile ? 0 : "95%"}
            display="flex"
            gap={100}
            justifyContent="left"
            pl={"1.5rem"}
            pt={"1.5rem"}
          >
            <Heading size="md">{translate("places.pinDetails")}</Heading>
          </Box>

          {isMobile ? null : (
            <Box width={"5%"} display="flex" pr={10} pt={6}>
              <CloseButton
                size={"sm"}
                onClick={() => {
                  if (pinSearchFromUrl) {
                    dispatch(setMarkers([]));
                    fetchPinDetailsWithSelectedData();
                  } else {
                    if (!isEmpty(centerCoordinates)) {
                      mapRef?.setView(
                        [centerCoordinates.lat, centerCoordinates.lng],
                        centerCoordinates.zoom
                      );
                      dispatch(setCurrentPageNumber(currentPageRef.current));

                      mapRef?.on("zoomend", () => {
                        dispatch(setCurrentPageNumber(currentPageRef.current));
                      });
                    }
                  }
                  dispatch(setMapCenterCoordinates({}));
                  dispatch(setView("activity-feed"));
                  dispatch(setPreviousMarker([]));
                  dispatch(setPinDetails({}));
                  dispatch(setPinSearchFromUrl(false));
                  const updatedPath = asPath?.split("?")?.[0];

                  router.replace(updatedPath, "", { shallow: true });
                }}
              />
            </Box>
          )}
        </HStack>
      </>

      <Box className="view-pin-details-scroller" overflowY={"auto"}>
        {viewPinDetails()}
      </Box>
    </>
  );
};

export default memo(MarkerDetails);
